import React, { useEffect, useState } from "react";
import classes from './CreateOrder.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import Footer from "../../Components/Footer/Footer";
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, Pencil, Trash2, X, FileText, Settings } from 'lucide-react';
import searchicon from "../../Assets/searchicon.png";
import filter from "../../Assets/filter.png";
import CreateProductionModal from "./CreateProductionModal";
import axios from "axios";
import { BASE_URL } from "../../Components/API/Api";
import Swal from 'sweetalert2';

const CreateOrder = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [isActive, setIsActive] = useState(true);
    const [selectedMetrics, setSelectedMetrics] = useState({
        total_purchase: 0,
        outstanding: 0,
    });

    const [loading, setLoading] = useState(true);
    const [modalLoading, setModalLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [deliveredOrders, setDeliveredOrders] = useState([]);
    const [companyId, setCompanyId] = useState(null);

    // Modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewEditModal, setShowViewEditModal] = useState(false);
    const [modalMode, setModalMode] = useState("view");
    const [selectedOrder, setSelectedOrder] = useState(null);

    // Modal form data
    const [modalData, setModalData] = useState({
        productionNo: "",
        product: "",
        productId: "",
        plannedQty: "",
        warehouse: "",
        warehouseId: "",
        startDate: "",
        endDate: "",
        materials: []
    });

    // Get company ID from localStorage
    useEffect(() => {
        const getCompanyId = () => {
            console.log("Attempting to get company ID from various sources...");
            
            // First check if we have a company_id directly in localStorage
            const directCompanyId = localStorage.getItem("company_id");
            if (directCompanyId) {
                console.log("Found company_id in localStorage:", directCompanyId);
                setCompanyId(directCompanyId);
                return;
            }
            
            // Check for companyInfo
            const companyInfo = localStorage.getItem("companyInfo");
            if (companyInfo) {
                try {
                    const parsed = JSON.parse(companyInfo);
                    const id = parsed?.id || parsed?.company_id || parsed?.companyId;
                    if (id) {
                        setCompanyId(String(id));
                        console.log("Using company ID from companyInfo:", id);
                        return;
                    }
                } catch (error) {
                    console.error("Error parsing companyInfo:", error);
                }
            }
            
            // Check user data
            const userData = localStorage.getItem("user");
            if (userData) {
                try {
                    const parsed = JSON.parse(userData);
                    
                    if (parsed.company_id) {
                        setCompanyId(parsed.company_id);
                        console.log("Using direct company_id from user:", parsed.company_id);
                        return;
                    }
                    
                    if (parsed.company) {
                        const companyId = parsed.company?.id || parsed.company?.company_id || parsed.company?.companyId;
                        if (companyId) {
                            setCompanyId(String(companyId));
                            console.log("Using company ID from user.company:", companyId);
                            return;
                        }
                    }
                    
                    if (parsed.companies && Array.isArray(parsed.companies) && parsed.companies.length > 0) {
                        const mainCompany = parsed.companies.find(c => c.is_main === "1" || c.is_main === 1);
                        if (mainCompany) {
                            const companyId = mainCompany?.id || mainCompany?.company_id || mainCompany?.companyId;
                            if (companyId) {
                                setCompanyId(String(companyId));
                                console.log("Using main company ID from companies array:", companyId);
                                return;
                            }
                        }
                        
                        const firstCompany = parsed.companies[0];
                        if (firstCompany) {
                            const companyId = firstCompany?.id || firstCompany?.company_id || firstCompany?.companyId;
                            if (companyId) {
                                setCompanyId(String(companyId));
                                console.log("Using first company ID from companies array:", companyId);
                                return;
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error parsing user data:", error);
                }
            }
            
            console.warn("No company ID found in any source");
        };
        
        getCompanyId();
    }, []);

    const fetchProductionOrders = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            
            if (!token) {
                console.error("No auth token found");
                Swal.fire({
                    icon: 'error',
                    title: 'Authentication Error',
                    text: 'Please log in again'
                });
                setLoading(false);
                return;
            }

            if (!companyId) {
                console.error("Company ID is required");
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Company ID not found. Please log in again.'
                });
                setLoading(false);
                return;
            }

            console.log(`Fetching production orders for company ID: ${companyId}`);
            
            const response = await axios.get(`${BASE_URL}/v1/production_order`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    company_id: companyId
                }
            });

            if (response.data.status && Array.isArray(response.data.data)) {
                setDeliveredOrders(response.data.data);
                
                // Update metrics if needed
                // You can calculate metrics based on the response data
                const totalPurchase = response.data.data.reduce((sum, order) => sum + (parseFloat(order.total_cost) || 0), 0);
                const outstanding = response.data.data.filter(order => order.status === 'pending').length;
                
                setSelectedMetrics({
                    total_purchase: totalPurchase,
                    outstanding: outstanding,
                });
            } else {
                setDeliveredOrders([]);
            }
        } catch (error) {
            console.error("Error fetching production orders:", error);
            
            if (error.response) {
                console.error("Error response data:", error.response.data);
                console.error("Error response status:", error.response.status);
                
                let errorMessage = "Failed to fetch production orders";
                if (error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.status === 401) {
                    errorMessage = "Unauthorized. Please log in again.";
                } else if (error.response.status === 404) {
                    errorMessage = "API endpoint not found";
                }
                
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: errorMessage
                });
            } else if (error.request) {
                console.error("No response received:", error.request);
                Swal.fire({
                    icon: 'error',
                    title: 'Network Error',
                    text: 'No response from server. Please check your connection.'
                });
            } else {
                console.error("Error setting up request:", error.message);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'An error occurred while setting up the request'
                });
            }
            
            setDeliveredOrders([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (companyId) {
            fetchProductionOrders();
        }
    }, [companyId]);

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
    };

    // Helper function to get material name from requisition
    const getMaterialName = (req) => {
        if (req.item && req.item.name) {
            return req.item.name;
        } else if (req.material) {
            if (isNaN(req.material) || req.material === "") {
                return req.material;
            } else {
                return `Material ID: ${req.material}`;
            }
        }
        return "N/A";
    };

    // Helper function to get UOM from requisition
    const getUOM = (req) => {
        return req.unit_of_measurement || req.uom || req.item?.unit || "N/A";
    };

    // Helper function to process order data for modal
    const processOrderData = (order) => {
        const materials = order.raw_material_requisitions?.map(req => {
            const materialName = getMaterialName(req);
            const uom = getUOM(req);

            const unitCost = req.unit_cost ? parseFloat(req.unit_cost).toFixed(2) : "0.00";
            const totalCost = req.total_cost ? parseFloat(req.total_cost).toFixed(2) : "0.00";
            const qtyIssued = req.quantity_issued ? parseFloat(req.quantity_issued).toFixed(2) : "0.00";
            const qtyRequired = req.quantity_required || "0";

            return {
                id: req.id,
                requisition_id: req.id,
                material: materialName,
                batchNo: req.batch_number || "N/A",
                qtyRequired: qtyRequired,
                qtyIssued: qtyIssued,
                uom: uom,
                unitCost: unitCost,
                totalCost: totalCost
            };
        }) || [];

        setModalData({
            productionNo: order.production_number || "",
            product: order.product?.name || "",
            productId: order.target_product_id || order.product?.id || "",
            plannedQty: order.planned_quantity || "",
            warehouse: order.warehouse?.name || "Not specified",
            warehouseId: order.ware_house_id || "",
            startDate: order.start_date || "",
            endDate: order.expected_end_date || "",
            materials: materials
        });
    };

    // Handle View/Edit icon clicks
    const handleViewClick = async (order) => {
        setModalMode("view");
        setSelectedOrder(order);
        setModalLoading(true);
        setShowViewEditModal(true);
        processOrderData(order);
        setModalLoading(false);
    };

    const handleEditClick = async (order) => {
        setModalMode("edit");
        setSelectedOrder(order);
        setModalLoading(true);
        setShowViewEditModal(true);
        processOrderData(order);
        setModalLoading(false);
    };

    // Handle modal input changes
    const handleModalChange = (field, value) => {
        setModalData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle material row changes
    const handleMaterialChange = (index, field, value) => {
        if (modalMode === "edit" && field === "qtyRequired") {
            const updatedMaterials = [...modalData.materials];
            const currentMaterial = updatedMaterials[index];

            const qtyRequired = parseFloat(value) || 0;
            const unitCost = parseFloat(currentMaterial.unitCost) || 0;
            const totalCost = (qtyRequired * unitCost).toFixed(2);

            updatedMaterials[index] = {
                ...currentMaterial,
                [field]: value,
                totalCost: totalCost
            };

            setModalData(prev => ({
                ...prev,
                materials: updatedMaterials
            }));
        }
    };

    const handleSave = async () => {
        if (modalMode === "edit") {
            setSaveLoading(true);
            try {
                const token = localStorage.getItem("authToken");

                // Double-check company ID
                let currentCompanyId = companyId;
                
                if (!currentCompanyId) {
                    currentCompanyId = localStorage.getItem("company_id");
                    
                    if (!currentCompanyId) {
                        const companyInfo = localStorage.getItem("companyInfo");
                        if (companyInfo) {
                            try {
                                const parsed = JSON.parse(companyInfo);
                                currentCompanyId = parsed?.id || parsed?.company_id || parsed?.companyId;
                            } catch (error) {
                                console.error("Error parsing companyInfo:", error);
                            }
                        }
                    }
                }

                if (!currentCompanyId) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Company ID Error',
                        text: 'Company ID not found. Please select a company and try again.',
                        confirmButtonText: 'Go to Dashboard'
                    }).then(() => {
                        navigate('/dashboard');
                    });
                    setSaveLoading(false);
                    return;
                }

                // Prepare the request data with all required fields
                const requestData = {
                    order_id: selectedOrder.id,
                    target_product_id: modalData.productId,
                    planned_quantity: modalData.plannedQty,
                    ware_house_id: modalData.warehouseId || null,
                    start_date: modalData.startDate,
                    expected_end_date: modalData.endDate,
                    materials: modalData.materials.map(mat => ({
                        requisition_id: mat.requisition_id || mat.id,
                        quantity_required: mat.qtyRequired
                    })),
                    company_id: currentCompanyId
                };

                console.log("Saving data:", requestData);

                const response = await axios.post(
                    `${BASE_URL}/v1/production_order/update`,
                    requestData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                    }
                );

                console.log("Update response:", response.data);

                if (response.data.status) {
                    fetchProductionOrders();
                    setShowViewEditModal(false);

                    Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: response.data.message || 'Production order updated successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    let errorMessage = "Failed to update production order";
                    if (response.data.message) {
                        if (typeof response.data.message === 'object') {
                            errorMessage = Object.values(response.data.message).flat().join(', ');
                        } else {
                            errorMessage = response.data.message;
                        }
                    }

                    Swal.fire({
                        icon: 'error',
                        title: 'Error!',
                        text: errorMessage
                    });
                }
            } catch (error) {
                console.error("Error updating production order:", error);

                let errorMessage = "Failed to update production order";

                if (error.response) {
                    if (error.response.data && error.response.data.message) {
                        if (typeof error.response.data.message === 'object') {
                            errorMessage = Object.values(error.response.data.message).flat().join(', ');
                        } else {
                            errorMessage = error.response.data.message;
                        }
                    } else if (error.response.status === 422) {
                        errorMessage = "Validation error. Please check all required fields.";
                    }
                } else if (error.request) {
                    errorMessage = "No response from server. Please check your connection.";
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: errorMessage,
                    confirmButtonText: 'OK'
                });
            } finally {
                setSaveLoading(false);
            }
        } else {
            setShowViewEditModal(false);
        }
    };

    // Handle delete order with SweetAlert
    const handleDeleteClick = async (order) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem("authToken");
                
                if (!companyId) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Company ID Error',
                        text: 'Company ID not found. Please try again.'
                    });
                    return;
                }
                
                const response = await axios.delete(
                    `${BASE_URL}/v1/production_order/${order.id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        params: {
                            company_id: companyId
                        }
                    }
                );

                if (response.data.status) {
                    fetchProductionOrders();

                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: response.data.message || 'Production order deleted successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    throw new Error(response.data.message || "Failed to delete production order");
                }
            } catch (error) {
                console.error("Error deleting production order:", error);

                let errorMessage = "Failed to delete production order";
                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: errorMessage,
                    confirmButtonText: 'OK'
                });
            }
        }
    };

    // Skeleton loader for table rows
    const TableSkeleton = () => {
        return Array.from({ length: 5 }).map((_, index) => (
            <tr key={index} className={classes.skeletonRow}>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                    </div>
                </td>
            </tr>
        ));
    };

    // Skeleton for modal form inputs
    const ModalInputSkeleton = () => (
        <div className={classes.skeletonInput}></div>
    );

    // Skeleton for modal table rows
    const ModalTableSkeleton = () => {
        return Array.from({ length: 3 }).map((_, index) => (
            <tr key={index}>
                {Array.from({ length: 6 }).map((_, cellIndex) => (
                    <td key={cellIndex}>
                        <div className={classes.skeletonInput} style={{ margin: "4px 0" }}></div>
                    </td>
                ))}
            </tr>
        ));
    };

    // Update the modal to allow date editing in edit mode
    const renderDateField = (label, value, field) => {
        if (modalMode === "edit") {
            return (
                <div className={classes.formGroup}>
                    <label>{label}</label>
                    <input
                        type="date"
                        value={value}
                        onChange={(e) => handleModalChange(field, e.target.value)}
                        className={classes.Field2}
                    />
                </div>
            );
        } else {
            return (
                <div className={classes.formGroup}>
                    <label>{label}</label>
                    <input
                        type="text"
                        value={value}
                        readOnly
                        className={classes.Field2}
                    />
                </div>
            );
        }
    };

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname]} />

            <div className={classes.Container}>
                <div className={classes.Container}>
                    <div className={classes.actions}>
                        <button className={classes.btns} onClick={() => navigate('/transactions/sales-invoice/sale')}>
                            Sales Invoice
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/Inventory/PurchaseOrder')}>
                            Purchase Order
                        </button>
                        <button className={`${classes.btns} ${isActive ? classes.active : ""}`} onClick={() => setIsActive(true)}>
                            Stock Delivery
                        </button>
                        <button onClick={() => navigate('/settings/product-services')} className={`${classes.btns} ${classes.greens}`}>
                            Product & Services
                        </button>
                    </div>

                    <header className={classes.Header}>
                        <h2 className={classes.Title}>Production Order</h2>
                    </header>

                    <div className={classes.entrjj}>
                        <div className={classes.allkads}>
                            <div className={classes.thrdkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total sales</p>
                                    <p className={classes.kdptg1}>
                                        {selectedMetrics?.total_purchase?.toLocaleString() ?? '0'}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>This month</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.frtkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Outstanding</p>
                                    <p className={classes.kdptg2}>
                                        {selectedMetrics?.outstanding?.toLocaleString() ?? '0'}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Pending payments</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.fiftkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Overdue</p>
                                    <p className={classes.kdptg3}>0</p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Past due dates</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.frstkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>This month</p>
                                    <p className={classes.kdptg4}>15</p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Total invoices</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={classes.headerActions}>
                        <div className={classes.actionsRight}>
                            <div className={classes.inputWrapper}>
                                <img src={searchicon} alt="Search" className={classes.inputIconGreen} />
                                <input type="text" className={classes.searchBar} placeholder="Search sales" />
                            </div>
                            <div className={classes.selectWrapper}>
                                <select className={classes.dropdown}>
                                    <option value="">All Status</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Overdue">Overdue</option>
                                    <option value="Draft">Draft</option>
                                </select>
                                <span className={classes.dropdownArrow}>▼</span>
                            </div>
                            <div className={classes.inputWrapper}>
                                <input type="text" className={classes.searchBars} placeholder="Filter..." />
                                <img src={filter} alt="Filter" className={classes.inputIcon} />
                            </div>
                        </div>

                        <button className={classes.btnss} onClick={() => setShowCreateModal(true)}>
                            + Create Production Order
                        </button>
                    </div>

                    <table className={classes.invoiceTable}>
                        <thead>
                            <tr>
                                <th>S/N</th>
                                <th>Production No</th>
                                <th>Product to Manufacture</th>
                                <th>Qty Req</th>
                                <th>Created By</th>
                                <th>Start Date</th>
                                <th>Due Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <TableSkeleton />
                            ) : deliveredOrders.length > 0 ? (
                                deliveredOrders.map((order, index) => (
                                    <tr key={order.id}>
                                        <td>{index + 1}</td>
                                        <td>{order.production_number}</td>
                                        <td>{order.product?.name || "—"}</td>
                                        <td>{order.planned_quantity}</td>
                                        <td>{order.created_by?.name || "—"}</td>
                                        <td>{order.start_date}</td>
                                        <td>{order.expected_end_date}</td>
                                        <td>
                                            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                                <Eye
                                                    size={16}
                                                    style={{ cursor: "pointer", color: "#007bff" }}
                                                    onClick={() => handleViewClick(order)}
                                                />
                                                <Pencil
                                                    size={16}
                                                    style={{ cursor: "pointer", color: "#28a745" }}
                                                    onClick={() => handleEditClick(order)}
                                                />
                                                <Trash2
                                                    size={16}
                                                    style={{ cursor: "pointer", color: "#dc3545" }}
                                                    onClick={() => handleDeleteClick(order)}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8" style={{ textAlign: "center" }}>No records found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Integrated View/Edit Modal */}
            {showViewEditModal && (
                <div className={classes.overlay}>
                    <div className={classes.modal}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <FileText className={classes.icon} />
                                <h2>{modalMode === "edit" ? "Edit Production Order" : "View Production Order"}</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={() => setShowViewEditModal(false)} disabled={saveLoading}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className={classes.formGrid}>
                            {modalLoading ? (
                                Array.from({ length: 7 }).map((_, index) => (
                                    <div className={classes.formGroup} key={index}>
                                        <div className={classes.skeletonLabel}></div>
                                        <ModalInputSkeleton />
                                    </div>
                                ))
                            ) : (
                                <>
                                    <div className={classes.formGroup}>
                                        <label>Production No</label>
                                        <input
                                            type="text"
                                            value={modalData.productionNo}
                                            readOnly
                                            className={classes.Field2}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Product to Manufacture</label>
                                        <input
                                            type="text"
                                            value={modalData.product}
                                            readOnly
                                            className={classes.Field2}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Planned Quantity</label>
                                        <input
                                            type="number"
                                            value={modalData.plannedQty}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("plannedQty", e.target.value)}
                                            className={modalMode === "edit" ? classes.number2 : classes.Field2}
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Warehouse</label>
                                        <input
                                            type="text"
                                            value={modalData.warehouse}
                                            readOnly
                                            className={classes.Field2}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Start Date</label>
                                        <input
                                            type={modalMode === "edit" ? "date" : "text"}
                                            value={modalData.startDate}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("startDate", e.target.value)}
                                            className={classes.Field2}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Expected End Date</label>
                                        <input
                                            type={modalMode === "edit" ? "date" : "text"}
                                            value={modalData.endDate}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("endDate", e.target.value)}
                                            className={classes.Field2}
                                        />
                                    </div>

                                    {/* Hidden inputs for IDs */}
                                    <input type="hidden" value={modalData.productId} />
                                    <input type="hidden" value={modalData.warehouseId} />
                                </>
                            )}
                        </div>

                        <div className={classes.materialsHeader}>
                            <Settings className={classes.settingsIcon} />
                            <h3>Materials Required</h3>
                        </div>

                        <div className={classes.tableWrapper}>
                            <table className={classes.table}>
                                <thead>
                                    <tr>
                                        <th>Material</th>
                                        <th>Qty Required</th>
                                        <th>Qty Issued</th>
                                        <th>UOM</th>
                                        <th>Unit Cost</th>
                                        <th>Total Cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modalLoading ? (
                                        <ModalTableSkeleton />
                                    ) : modalData.materials.length > 0 ? (
                                        modalData.materials.map((row, index) => (
                                            <tr key={row.id || index}>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={row.material}
                                                        readOnly
                                                        className={classes.Field2}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        value={row.qtyRequired}
                                                        readOnly={modalMode === "view" || saveLoading}
                                                        onChange={(e) => handleMaterialChange(index, "qtyRequired", e.target.value)}
                                                        className={modalMode === "view" ? classes.number2 : classes.Field2}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={row.qtyIssued}
                                                        readOnly
                                                        className={classes.Field2}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={row.uom}
                                                        readOnly
                                                        className={classes.Field2}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={row.unitCost}
                                                        readOnly
                                                        className={classes.number2}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={row.totalCost}
                                                        readOnly
                                                        className={classes.total2}
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>
                                                No materials found for this production order
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <button
                            className={classes.saveBtn}
                            onClick={handleSave}
                            disabled={saveLoading}
                        >
                            {saveLoading ? (
                                <span className={classes.loadingText}>
                                    <span className={classes.spinner}></span>
                                    Saving...
                                </span>
                            ) : (
                                modalMode === "edit" ? "Save Changes" : "Close"
                            )}
                        </button>
                    </div>
                </div>
            )}

            <CreateProductionModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                refreshOrders={fetchProductionOrders}
                companyId={companyId}
            />

            <Footer />
        </div>
    );
};

export default CreateOrder;